import _ from "lodash";
import moment from "moment";
import { getMemoizedLangFunction, getWeekOrdinalText } from "Utils";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getStaffFragmentFromCache } from "AppComponents/EntitySharing/modules/EntitySharingGraphqlHelpers";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
  VIEW_MODE,
  SCHOOL_LOGO,
  IB_LOGO_IMAGE,
  LEARNING_ENGAGEMENT,
  ASSESSMENT,
  WORKSHEET,
  AI_TUTOR,
  ASSESSMENT_TYPE_QUICK_TASK,
} from "Constants/stringConstants";
import {
  ENABLE_AI_TUTOR,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  CLASS_PLANNER_EDIT_PERMISSION,
  CLASS_PLANNER_ADMIN_PERMISSION,
} from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import {
  getAssignmentDropdownOptions,
  getDynamicAssessmentTypeOptions,
} from "ClassRoom/modules/utils";
import {
  getIsAssessmentTypeConfigurationEnabled,
  getIsTimeAxisEnabledInPlanner,
} from "Utils/ACLStoreUtils";
import { TIME_AXIS } from "ClassPlanners/modules/Constants";
import { getSectionId } from "WeeklyPlanning/modules/utils";
import ACLStorage from "lib/aclService";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

export const getWeekDurationFormat = ({ startDate, endDate }) => {
  if (moment(startDate).isSame(endDate, "day")) {
    return moment(startDate).format("MMM D, YYYY");
  }

  const startYear = moment(startDate).format("YYYY");
  const endYear = moment(endDate).format("YYYY");

  const startMonth = moment(startDate).format("MMM");
  const endMonth = moment(endDate).format("MMM");

  return startYear !== endYear
    ? `${moment(startDate).format("MMM D, YYYY")} - ${moment(endDate).format(
        "MMM D, YYYY"
      )}`
    : startMonth !== endMonth
    ? `${moment(startDate).format("MMM D")} - ${moment(endDate).format(
        "MMM D, YYYY"
      )}`
    : `${moment(startDate).format("MMM D")} - ${moment(endDate).format(
        "D, YYYY"
      )}`;
};

export const getCombinedDateAndTime = ({ date, time }) => {
  const dateMoment = moment(date);
  const timeMoment = moment(time);

  return moment(dateMoment).set({
    hour: timeMoment.get("hour"),
    minute: timeMoment.get("minute"),
    second: timeMoment.get("second"),
  });
};

export const calculateAbsoluteCardSidePositionWithScreenSize = ({
  parentNode,
  node,
  cardDimensions,
  screenPadding = {
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 8,
    paddingRight: 8,
  },
  margin = { x: 8, y: 0 },
  isSingleView = false,
}) => {
  const CARD_HEIGHT = cardDimensions.height;
  const CARD_WIDTH = cardDimensions.width;

  const parentPos = parentNode.getBoundingClientRect();

  // Get the current screen width
  const screenWidth =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;

  const parentHeight =
    parentPos.height - screenPadding.paddingTop - screenPadding.paddingBottom;
  const parentWidth =
    parentPos.width - screenPadding.paddingLeft - screenPadding.paddingRight;

  const rect = node.getBoundingClientRect();
  const originX = rect.x - parentPos.x;
  const originY = rect.y - parentPos.y;

  let xPos = originX + rect.width + margin.x;

  if (xPos + CARD_WIDTH > parentWidth) {
    xPos = originX - margin.x - CARD_WIDTH;
  }

  let yPos = originY + margin.y;

  if (yPos > parentHeight / 2) {
    yPos = yPos - CARD_HEIGHT + rect.height;
  }

  if (yPos + CARD_HEIGHT > parentHeight) {
    yPos = parentHeight - CARD_HEIGHT;
  }

  if (yPos < screenPadding.paddingTop) {
    yPos = screenPadding.paddingTop;
  }

  // If the position of X < 0 or the card extends beyond the screen width, we align it to the center
  if (xPos < 0 || xPos + CARD_WIDTH > screenWidth) {
    xPos = originX + rect.width / 2 + margin.x - CARD_WIDTH / 2;
  }

  if (isSingleView) {
    // In single day view, place modal to the right of the day column.
    // Clamp to viewport right edge instead of parent container bounds,
    // so it never goes off-screen but stays as far right as possible.
    xPos = originX + rect.width + margin.x;
    const maxXByViewport =
      screenWidth - parentPos.x - CARD_WIDTH - screenPadding.paddingRight;
    if (xPos > maxXByViewport) {
      xPos = maxXByViewport;
    }
    if (xPos < screenPadding.paddingLeft) {
      xPos = screenPadding.paddingLeft;
    }
    return { xPos, yPos };
  }

  return { xPos, yPos };
};

// Function to get the options for weekly recurring schedules
export const getWeekDaysOptions = ({ t }) => {
  const localizedDays = [
    { label: t("common:sun"), value: 0 },
    { label: t("common:mon"), value: 1 },
    { label: t("common:tue"), value: 2 },
    { label: t("common:wed"), value: 3 },
    { label: t("common:thu"), value: 4 },
    { label: t("common:fri"), value: 5 },
    { label: t("common:sat"), value: 6 },
  ];
  return localizedDays;
};

export const getOperationalDays = ({ t, operationalDays }) => {
  const localizedDays = getWeekDaysOptions({ t });
  return _.filter(localizedDays, item =>
    _.includes(operationalDays, item.value)
  );
};

// Function to get the options for recurrence
export const getRecurrenceOptions = ({ t, day }) => {
  return [
    { label: t("common:daily"), value: "DAILY" },
    {
      label: t("classPlanner:weekly_recurrence_option", { day }),
      value: "WEEKLY",
    },
    { label: t("common:custom"), value: "CUSTOM" },
    {
      label: t("common:does_not_repeat"),
      value: "DOES_NOT_REPEAT",
    },
  ];
};

export const getRepeatEveryFrequencyOptions = ({ t }) => {
  return [
    { label: t("common:day_one_or_more"), value: "DAILY" },
    { label: t("common:week"), value: "WEEKLY" },
    { label: t("common:month"), value: "MONTHLY" },
  ];
};

// Function to get the options for monthly recurring schedules
export const getMonthlyRecurringOptions = ({
  dayOfMonth,
  weekOfMonth,
  weekDayName,
  t,
}) => {
  const options = [
    {
      label: t("common:on_the_day_label", { day: dayOfMonth }),
      value: "by_date",
    },
    {
      label: t("common:on_the_week_number_day_label", {
        weekNumber: getWeekOrdinalText({ weekOfMonth, t }),
        weekDay: weekDayName,
      }),
      value: "by_week",
    },
  ];
  return options;
};

export const extractSectionDetails = ({ dynamicSectionId }) => {
  const [type, id, date] = _.split(dynamicSectionId, "=>");
  return { type, id: getSectionId(id), date };
};

export const getAssessmentType = ({
  t,
  assessmentType,
  currentAssessmentTypes,
}) => {
  const { localeLabel = "", label = "" } =
    currentAssessmentTypes[assessmentType] || {};
  return localeLabel ? t(localeLabel) : label;
};

export const getNextOperationalDate = ({
  date,
  classPlannerEndDate,
  operationalDays,
}) => {
  const nextDay = moment(date).add(1, "days");
  const dateWeekDay = nextDay.weekday();
  const isOperationalDay = _.includes(operationalDays, dateWeekDay);
  const classPlannerEndDateMoment = moment(classPlannerEndDate).endOf("day");
  let operationalDate;
  if (isOperationalDay) operationalDate = nextDay;
  else {
    // Calculate the days until the next Monday (1 for Monday, 2 for Tuesday, and so on)
    const daysUntilNextMonday = (8 - moment(date).day()) % 7;

    // Add the calculated number of days to the given date to get the next Monday's date
    const nextMonday = moment(date).add(daysUntilNextMonday, "days");

    // If the next Monday is after the class planner end date, return the class planner end date
    if (nextMonday.isAfter(classPlannerEndDateMoment)) {
      return classPlannerEndDateMoment;
    }

    return nextMonday;
  }

  if (operationalDate.isAfter(classPlannerEndDateMoment)) {
    return classPlannerEndDateMoment;
  }

  return operationalDate;
};

export const getSharedCourseIdsAndTitles = ({ courses }) => {
  const sharedCourseIds = [];
  const sharedCourseTitles = [];
  courses.forEach(course => {
    sharedCourseIds.push(course.id);
    sharedCourseTitles.push(course.title);
  });
  return { sharedCourseIds, sharedCourseTitles };
};

export const getExceptionCollaboratorList = ({ exceptionalCollaborators }) => {
  const ExceptionCollaboratorList = _.get(
    exceptionalCollaborators,
    "edges",
    []
  );
  return _.reduce(
    ExceptionCollaboratorList,
    (result, collaborator) => {
      result[collaborator.node.id] = collaborator.permission;
      return result;
    },
    {}
  );
};

export const getFilteredExceptionCollaboratorList = ({
  exceptionalCollaborators,
  isRemovingCollaborator,
  allCourses,
}) => {
  return _.reduce(
    exceptionalCollaborators,
    (result, value, key) => {
      let staffid = value;
      if (!isRemovingCollaborator) {
        staffid = value.id;
      }

      const staffDetails = getStaffFragmentFromCache({
        id: staffid,
        variables: { skipTitle: false },
      });
      const staffCourses = _.map(staffDetails.courses.edges, "node.id");
      const isCoursePresent = _.intersection(allCourses, staffCourses);
      if (isCoursePresent.length !== 0) {
        if (!isRemovingCollaborator) {
          result.push(value);
        } else result.push(staffid);
      }
      return result;
    },
    []
  );
};

export const getPlannerPermissionType = ({
  defaultAccessState,
  collaborators,
  exceptionalCollaborators,
  userId,
}) => {
  if (
    _.isUndefined(defaultAccessState) &&
    _.isUndefined(collaborators) &&
    _.isUndefined(exceptionalCollaborators)
  ) {
    return "NO_ACCESS";
  }
  const allCollaborators = _.concat(
    collaborators?.edges,
    exceptionalCollaborators?.edges
  );

  const collaboratorsPermissionDetails = _.find(allCollaborators, {
    node: { id: userId },
  });

  const userPermissionType = _.isEmpty(
    collaboratorsPermissionDetails?.permission
  )
    ? defaultAccessState
    : collaboratorsPermissionDetails?.permission;

  return userPermissionType;
};

export const checkUnitPlanEditAccess = ({ sectionItem, userId }) => {
  const allCollaborators = _.get(
    sectionItem,
    "unitPlan.allCollaborators.edges",
    []
  );
  const collaboratorsPermissionDetails = _.find(allCollaborators, {
    node: { id: userId },
  });

  const isEditPermission = _.isEqual(
    collaboratorsPermissionDetails?.permission,
    "EDIT"
  );

  return isEditPermission;
};

export const checkPlannerHasViewAccess = ({
  defaultAccessState,
  collaborators,
  exceptionalCollaborators,
  userId,
  isUserParent,
}) => {
  const userPermissionType = getPlannerPermissionType({
    defaultAccessState,
    collaborators,
    exceptionalCollaborators,
    userId,
  });
  if (_.isEqual(userPermissionType, "VIEW") || isUserParent) return true;
  return false;
};

export const checkPlannerHasNoAccess = ({
  defaultAccessState,
  collaborators,
  exceptionalCollaborators,
  userId,
  isUserParent,
  isSharedWithFamilies,
}) => {
  if (isUserParent && isSharedWithFamilies) return false;
  const userPermissionType = getPlannerPermissionType({
    defaultAccessState,
    collaborators,
    exceptionalCollaborators,
    userId,
  });
  if (_.isEqual(userPermissionType, "NO_ACCESS")) return true;
  return false;
};

export const getFilteredCardOptions = ({
  t,
  selectedCurriculumType,
  curriculumId,
}) => {
  const timeAxisToggleOption = getIsTimeAxisEnabledInPlanner()
    ? [
        {
          key: "d-1",
          isDivider: true,
        },
        {
          label: t("classPlanner:time_axis_checkbox_label"),
          value: TIME_AXIS,
        },
      ]
    : [];

  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  if (isAssessmentTypeConfigurationEnabled) {
    const dynamicOptions = getDynamicAssessmentTypeOptions({ curriculumId });

    const assessmentTypeOptions = _.map(
      _.filter(
        dynamicOptions,
        option => _.get(option, "taskType") !== ASSESSMENT_TYPE_QUICK_TASK
      ),
      option => ({
        label: _.get(option, "label", ""),
        value: _.get(option, "taskType", ""),
      })
    );

    const result = [
      { label: t("common:description"), value: "DESCRIPTION" },
      { label: t("common:note_plural"), value: "NOTE" },
      ...assessmentTypeOptions,
      {
        label: t("classPlanner:resources_and_files"),
        value: "ATTACHMENT",
      },
    ];

    return [...result, ...timeAxisToggleOption];
  }

  const aiChatTaskOption = ACLStore.can(ENABLE_AI_TUTOR)
    ? [
        {
          label: t("common:ai_tutor"),
          value: AI_TUTOR,
        },
      ]
    : [];
  let result = [];

  switch (selectedCurriculumType) {
    case CURRICULUM_TYPE_DP:
    case CURRICULUM_TYPE_MYP:
      result = [
        { label: t("common:description"), value: "DESCRIPTION" },
        { label: t("common:note_plural"), value: "NOTE" },
        { label: t("common:quick_task"), value: "QUICK_TASK" },
        { label: t("common:le_label_plural"), value: "LEARNING_EXPERIENCES" },
        {
          label: t("common:formative_assessments"),
          value: "FORMATIVE_ASSESSMENT",
        },
        {
          label: t("common:summative_assessments"),
          value: "SUMMATIVE_ASSESSMENT",
        },
        ...(ACLStore.can("FeatureFlag:ClassroomWorksheet")
          ? [
              {
                label: t("common:worksheet_other"),
                value: "WORKSHEET",
              },
            ]
          : []),
        ...aiChatTaskOption,
        {
          label: t("classPlanner:resources_and_files"),
          value: "ATTACHMENT",
        },
      ];
      break;

    case CURRICULUM_TYPE_UBD:
      result = [
        { label: t("common:description"), value: "DESCRIPTION" },
        { label: t("common:note_plural"), value: "NOTE" },
        { label: t("common:quick_task"), value: "QUICK_TASK" },
        { label: t("common:le_label_plural"), value: "LEARNING_EXPERIENCES" },
        { label: t("common:assessment"), value: "ASSESSMENT" },
        ...(ACLStore.can("FeatureFlag:ClassroomWorksheet")
          ? [
              {
                label: t("common:worksheet_other"),
                value: "WORKSHEET",
              },
            ]
          : []),
        ...aiChatTaskOption,
        {
          label: t("classPlanner:resources_and_files"),
          value: "ATTACHMENT",
        },
      ];
      break;

    case CURRICULUM_TYPE_PYP:
      result = [
        { label: t("common:description"), value: "DESCRIPTION" },
        { label: t("common:note_plural"), value: "NOTE" },
        { label: t("common:quick_task"), value: "QUICK_TASK" },
        { label: t("common:le_label_plural"), value: "LEARNING_EXPERIENCES" },
        ...(ACLStore.can("FeatureFlag:EnablePYPAssessment")
          ? [
              {
                label: t("common:formative_assessments"),
                value: "FORMATIVE_ASSESSMENT",
              },
              {
                label: t("common:summative_assessments"),
                value: "SUMMATIVE_ASSESSMENT",
              },
            ]
          : []),
        ...(ACLStore.can("FeatureFlag:ClassroomWorksheet")
          ? [
              {
                label: t("common:worksheet_other"),
                value: "WORKSHEET",
              },
            ]
          : []),
        ...aiChatTaskOption,
        {
          label: t("classPlanner:resources_and_files"),
          value: "ATTACHMENT",
        },
      ];
      break;

    default:
      return [];
  }
  return [...result, ...timeAxisToggleOption];
};
export const getConfigurationFiltersLength = ({
  configurationFilterOptions,
}) => {
  const timeAxisToggleOption = getIsTimeAxisEnabledInPlanner();
  //Since we have a divider which gets counted as an item that needs to be subtracted for correct length
  return configurationFilterOptions.length - (timeAxisToggleOption ? 1 : 0);
};

export const getFilteredCardOptionsMemoized = _.memoize(
  params => getFilteredCardOptions(params),
  params => JSON.stringify(params)
);
export const getConfigurationFiltersLengthMemoized = _.memoize(
  params => getConfigurationFiltersLength(params),
  params => JSON.stringify(params)
);

const getChildrenOptions = ({ templateFieldOptions, option }) => {
  switch (option.value) {
    case "LEARNING_EXPERIENCES":
      return _.get(
        _.find(templateFieldOptions, { type: "LEARNING_ENGAGEMENT" }),
        "fieldOptions",
        []
      );
    case "QUICK_TASK":
      return _.get(
        _.find(templateFieldOptions, { assessmentType: "qt" }),
        "fieldOptions",
        []
      );
    case "FORMATIVE_ASSESSMENT":
      return _.get(
        _.find(templateFieldOptions, { assessmentType: "fmt" }),
        "fieldOptions",
        []
      );
    case "SUMMATIVE_ASSESSMENT":
      return _.get(
        _.find(templateFieldOptions, { assessmentType: "smt" }),
        "fieldOptions",
        []
      );
    case "WORKSHEET":
      return _.get(
        _.find(templateFieldOptions, { type: "WORKSHEET" }),
        "fieldOptions",
        []
      );
    case AI_TUTOR:
      return _.get(
        _.find(templateFieldOptions, { type: AI_TUTOR }),
        "fieldOptions",
        []
      );
    case "ASSESSMENT":
      return _.get(
        _.find(templateFieldOptions, { type: "ASSESSMENT" }),
        "fieldOptions",
        []
      );
    default:
      return [
        {
          label: option.label,
          id: option.value,
        },
      ];
  }
};

export const getAccordionItems = ({
  t,
  selectedCurriculumType,
  templateFieldOptions,
}) => {
  const isTimeAxisEnabled = getIsTimeAxisEnabledInPlanner();
  //removing the last two elements since one is the divider and the other is the time axis toggle
  const filteredCardOptions = isTimeAxisEnabled
    ? _.dropRight(
        getFilteredCardOptionsMemoized({
          t,
          selectedCurriculumType,
        }),
        2
      )
    : getFilteredCardOptionsMemoized({
        t,
        selectedCurriculumType,
      });

  const accordianItems = [];
  _.forEach(filteredCardOptions, option => {
    const childrenOptions = getChildrenOptions({
      templateFieldOptions,
      option,
    });
    if (!_.isEmpty(childrenOptions))
      accordianItems.push({
        parent: {
          label: option.label,
          id: option.value,
        },
        children: _.map(childrenOptions, childOption => {
          return {
            label: childOption.label,
            id: `${option.value}:${childOption.id}`,
          };
        }),
      });
  });
  return accordianItems;
};

export const getFilteredPlannerOptions = ({ t, selectedCurriculumType }) => {
  switch (selectedCurriculumType) {
    case CURRICULUM_TYPE_DP:
    case CURRICULUM_TYPE_MYP:
    case CURRICULUM_TYPE_PYP:
      return [
        { label: t("classPlanner:include_school_logo"), value: SCHOOL_LOGO },
        { label: t("classPlanner:include_IB_logo"), value: IB_LOGO_IMAGE },
      ];

    case CURRICULUM_TYPE_UBD:
      return [
        { label: t("classPlanner:include_school_logo"), value: SCHOOL_LOGO },
      ];

    default:
      return [];
  }
};

export const getFilteredPlannerOptionsMemoized = _.memoize(
  params => getFilteredPlannerOptions(params),
  params => JSON.stringify(params)
);

const getFilterSectionItems = ({ sectionItems, configurationFilters }) => {
  const showAssessmentTypeObject = {
    qt: _.includes(configurationFilters, "QUICK_TASK"),
    le: _.includes(configurationFilters, "LEARNING_EXPERIENCES"),
    fmt: _.includes(configurationFilters, "FORMATIVE_ASSESSMENT"),
    smt: _.includes(configurationFilters, "SUMMATIVE_ASSESSMENT"),
    worksheet: _.includes(configurationFilters, "WORKSHEET"),
    ai_tutor: _.includes(configurationFilters, AI_TUTOR),
  };

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const filterSectionItems = _.filter(sectionItems, item => {
    let isSectionItemMatched;

    const itemType = item?.itemType;
    switch (itemType) {
      case "ASSIGNMENT": {
        isSectionItemMatched = _.includes(configurationFilters, "ASSESSMENT");
        break;
      }
      case "ASSESSMENT": {
        const assessmentType = item?.sectionItem?.assessmentType?.value;

        if (assessmentType === "le") {
          isSectionItemMatched = showAssessmentTypeObject[assessmentType];
        } else {
          isSectionItemMatched =
            _.includes(configurationFilters, itemType) ||
            showAssessmentTypeObject[assessmentType];
        }
        break;
      }
      case "ATTACHMENT": {
        if (!isLearningCoursesEnabled && item?.sectionItem?.type === "NOTE") {
          isSectionItemMatched = _.includes(
            configurationFilters,
            item?.sectionItem?.type
          );
        } else {
          isSectionItemMatched = _.includes(configurationFilters, itemType);
        }

        break;
      }
      case "LEARNING_COURSE_FLOW": {
        const type = _.get(item, "sectionItem.itemType", "");
        // Map FILE to ATTACHMENT, and both ASSESSMENT/ASSIGNMENT to ASSESSMENT for filtering
        const filterType = _.isEqual(type, "FILE")
          ? "ATTACHMENT"
          : _.includes(["ASSESSMENT", "ASSIGNMENT"], type)
          ? "ASSESSMENT"
          : type;
        isSectionItemMatched = _.includes(configurationFilters, filterType);
        break;
      }
      default:
        break;
    }

    return isSectionItemMatched;
  });

  return filterSectionItems;
};

export const getFilterSectionItemsMemoized = _.memoize(
  params => getFilterSectionItems(params),
  params => JSON.stringify(params)
);

export const getHolidayEvents = ({ date, filteredHolidayEvents }) => {
  const matchingEvents = filteredHolidayEvents.filter(event => {
    const startDate = moment(event.start);
    const endDate = moment(event.end);
    const currentDate = moment(date);

    return currentDate.isBetween(startDate, endDate, null, "[]");
  });

  return matchingEvents;
};

// Function to get the ordered section items
const getReorderItems = ({ items, mode }) => {
  if (mode === VIEW_MODE) {
    const filteredItems = _.filter(items, item => {
      const { itemType, sectionItem } = item;
      if (itemType === "ATTACHMENT" && sectionItem.type === "NOTE")
        return sectionItem.title ? item : null;
      else return item;
    });
    return filteredItems;
  }
  return items;
};

export const getReorderItemsMemoized = _.memoize(
  params => getReorderItems(params),
  params => JSON.stringify(params)
);

export const getFormattedTimeRange = ({ startTime, endTime }) => {
  let formattedStartTime = moment(startTime).format(
    DATE_TIME_CONSTANTS.hh_mm_a
  );
  let formattedEndTime = moment(endTime).format(DATE_TIME_CONSTANTS.hh_mm_a);
  if (
    ((formattedStartTime.includes("am") && formattedEndTime.includes("am")) ||
      (formattedStartTime.includes("pm") && formattedEndTime.includes("pm"))) &&
    moment(startTime).isSame(moment(endTime), "day")
  )
    formattedStartTime = _.replace(formattedStartTime, / am| pm/, "");
  if (!moment(startTime).isSame(moment(endTime), "day")) {
    formattedStartTime = `(${moment(startTime).format(
      "Do MMM"
    )}) ${formattedStartTime}`;
    formattedEndTime = `(${moment(endTime).format(
      "Do MMM"
    )}) ${formattedEndTime}`;
  }
  return `${formattedStartTime} - ${formattedEndTime}`;
};

const isEmptyValue = value => {
  if (_.isNumber(value)) {
    return false; // Numbers are considered non-empty
  }

  return _.isEmpty(value);
};

export const checkRecurrenceSettingChanged = ({
  recurrenceSetting,
  recurrenceSettingDetailsFromParent,
}) => {
  const recurrenceSettingKeys = Object.keys(recurrenceSetting).sort();
  const recurrenceSettingDetailsFromParentKeys = Object.keys(
    recurrenceSettingDetailsFromParent
  ).sort();

  if (
    JSON.stringify(recurrenceSettingKeys) !==
    JSON.stringify(recurrenceSettingDetailsFromParentKeys)
  )
    return true;
  //if both recurrenceSetting recurrenceSettingDetailsFromParent are identical then we don't need to check value by value and can straight up say that RecurrenceSettingChanged is not changed
  if (_.isEqual(recurrenceSetting, recurrenceSettingDetailsFromParent))
    return false;

  for (const key of recurrenceSettingKeys) {
    const keyEmpty = isEmptyValue(recurrenceSetting[key]);
    const parentKeyEmpty = isEmptyValue(
      recurrenceSettingDetailsFromParent[key]
    );
    // if one out of keyEmpty and parentKeyEmpty are empty and other one out of keyEmpty and parentKeyEmpty is not then object is changed
    // if both keyEmpty and parentKeyEmpty have same (_.isEmpty) value and they are both non empty (_.isEmpty is false) then we check whether we have different values or not
    if (
      keyEmpty !== parentKeyEmpty ||
      (!keyEmpty &&
        !_.isEqual(
          recurrenceSetting[key],
          recurrenceSettingDetailsFromParent[key]
        ))
    ) {
      return true;
    }
  }

  return false;
};

const getTemplateTypeFilter = ({ currentCurriculumProgramType }) => {
  const commonFilter = [LEARNING_ENGAGEMENT];
  const pypAssessmentFilter = ACLStore.can("FeatureFlag:EnablePYPAssessment")
    ? [ASSESSMENT]
    : [];
  const worksheetFilter = ACLStore.can("FeatureFlag:ClassroomWorksheet")
    ? [WORKSHEET]
    : [];
  const aiChatTaskFilter = ACLStore.can(ENABLE_AI_TUTOR) ? [AI_TUTOR] : [];

  if (currentCurriculumProgramType === CURRICULUM_TYPE_PYP) {
    return [
      ...commonFilter,
      ...pypAssessmentFilter,
      ...worksheetFilter,
      ...aiChatTaskFilter,
    ];
  }

  return [ASSESSMENT, ...commonFilter, ...worksheetFilter, ...aiChatTaskFilter];
};

export const getTemplateTypeFilterMemoized = _.memoize(
  params => getTemplateTypeFilter(params),
  params => JSON.stringify(params)
);

const getTemplateFieldOptions = ({ templates }) => {
  const templateFieldOptions = _.map(templates, template => {
    const { id, type, assessmentType } = template;

    const printSteps = _.get(template, "body.print_steps", []);
    const stepList = _.get(template, "body.step_list", []);
    const field_list = _.get(template, "body.field_list", []);

    const templateFields = _.reduce(
      printSteps,
      (result, step) => {
        let stepFields = stepList[step]?.fields;

        // Remove score, finalRemarks and enableRating fields from the list of fields
        stepFields = _.filter(
          stepFields,
          field =>
            !_.includes(
              [
                "score",
                "finalRemarks",
                "enableRating",
                "addLearningGoals",
                "plannerResources",
              ],
              field
            )
        );
        result.push(...stepFields);

        return result;
      },
      []
    );

    const templateFieldsWithLabel = _.map(templateFields, field => {
      const label =
        _.get(field_list[field], "config.viewLabel", "") ||
        _.get(field_list[field], "config.label", "") ||
        _.find(stepList, step => _.includes(step.fields, field))?.label;

      return {
        label,
        id: field,
      };
    });

    return {
      fieldOptions: templateFieldsWithLabel,
      id,
      type,
      assessmentType,
    };
  });
  return templateFieldOptions;
};

export const getTemplateFieldOptionsMemoized = _.memoize(
  params => getTemplateFieldOptions(params),
  params => JSON.stringify(params)
);

/*
The getRecurrenceDates() returns startDate and endDate depending on the below condition
If the selected Date in the section modal is out of the range of the recurrenceStartDate and recurrenceEndDate then we have to shift the section
with the new recurrenceStartDate and recurrenceEndDate
For that we calculate the length of the recurrence by calculating the difference between the current recurrenceStartDate and recurrenceEndDate and adding it 
to the new recurrenceStartDate to create the new recurrenceEndDate

scheduleDate: The current date of the section
startDate: The new selected date of that section by the user
recurranceEndDate: The date from which this recurring section starts
recurranceStartDate: The date from which this recurring section ends
duration: The duration of the recurring section
*/

export const getRecurrenceDates = ({
  scheduleDate,
  startDate,
  recurranceStartDate,
  recurranceEndDate,
  duration,
}) => {
  const start = moment(startDate);

  if (scheduleDate.isSame(start, "day")) {
    return {
      startDate: getCombinedDateAndTime({
        date: recurranceStartDate,
        time: start,
      }),
      endDate: recurranceEndDate,
    };
  }
  const newEndDate = moment(startDate).add(duration, "days");
  return { startDate: start, endDate: newEndDate };
};

// it was causing infinite renders
export const getOperationalDaysMemoized = _.memoize(({ operationalDays }) => {
  return operationalDays ? JSON.parse(operationalDays) : [];
}, JSON.stringify);

export const getAcademicYearDatesMemoized = _.memoize(node => {
  const academicYears = _.get(node, "academicYears", {});
  // Find the earliest startDate
  const { startDate } = _.minBy(academicYears, item => moment(item.startDate));

  // Find the latest endDate
  const { endDate } = _.maxBy(academicYears, item => moment(item.endDate));
  return {
    academicYearStartDate: startDate,
    academicYearEndDate: endDate,
  };
}, JSON.stringify);

export const hasEditAccess = ({ isUserParent }) => {
  const isRnPEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
  const isAdminPermissionEnabled = ACLStorage.getPermissionValue(
    CLASS_PLANNER_ADMIN_PERMISSION
  );
  const isEditPermissionEnabled = ACLStorage.getPermissionValue(
    CLASS_PLANNER_EDIT_PERMISSION
  );
  const canEditPlanner =
    (isRnPEnabled
      ? isAdminPermissionEnabled || isEditPermissionEnabled
      : true) && !isUserParent;
  return canEditPlanner;
};

export const getIsCalendarRevampPhase1Enabled = () => {
  return ACLStore.can("FeatureFlag:EnableCalendarRevampPhase1");
};

// Builds the object to persist in UserWeeklyPlannerFilterSettings.configurationFilters.
// Merges previousStored (to retain keys no longer in the current generated universe)
// then overrides every currently-visible key based on the selection array from the UI.
export const buildConfigurationFiltersObject = ({
  selectedKeys,
  generatedFilterOptions,
  previousStored,
}) => {
  const allKeys = _.compact(_.map(generatedFilterOptions, "value"));

  let base = {};
  if (_.isArray(previousStored)) {
    base = _.mapValues(_.keyBy(previousStored), () => true);
  } else if (_.isPlainObject(previousStored)) {
    base = { ...previousStored };
  }

  const selectedSet = new Set(selectedKeys);
  _.forEach(allKeys, k => {
    base[k] = selectedSet.has(k);
  });

  return base;
};

export const getCreateLeOptions = getMemoizedLangFunction(
  ({
    t,
    isTermsExistsForCurrentCurriculumProgram,
    isLeAssessmentCreationEnabled,
    curriculumType,
    curriculumId,
    globalGradeUid,
    unitPlanCount,
  }) => {
    if (!isLeAssessmentCreationEnabled) {
      return [];
    }

    const assignmentDropdownOptions = getAssignmentDropdownOptions({
      curriculumType,
      curriculumId,
      globalGradeUid,
    });
    const createGroup = _.find(assignmentDropdownOptions, { key: "create" });

    return _.compact(
      _.map(_.get(createGroup, "options", []), opt => {
        if (opt.perm && !ACLStore.can(opt.perm)) {
          return null;
        }

        const label = opt.taskType ? opt.label : t(opt.label);

        return {
          key: opt.key,
          label,
          icon: opt.icon,
          isLabelLocalized: !!opt.taskType,
          disabled:
            !isTermsExistsForCurrentCurriculumProgram ||
            !(unitPlanCount || opt.key == "CREATE_QUICK_TASK"),
          disabledTooltip: !isTermsExistsForCurrentCurriculumProgram
            ? t(opt.noTermsDisabledTooltip, { label })
            : t(
                "classRoom:assessment_creation_no_unit_plans_tooltip_with_label_plural",
                {
                  label,
                }
              ),
        };
      })
    );
  }
);
